import { useEffect, useState } from "react";
import { LifeBuoy, MessageSquareMore } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Panel } from "@/components/ui/panel";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PQRFormDialog } from "@/features/pqr";
import { AccountDeletionForm } from "@/features/profile/components/AccountDeletionForm";
import { PasswordChangeForm } from "@/features/profile/components/PasswordChangeForm";
import { useProfile } from "@/features/profile/hooks/useProfile";
import {
  profileSchema,
  type ProfileFormValues,
  type ProfileSubmissionValues,
} from "@/features/profile/validation/profileSchemas";
import { useAuthStore } from "@/stores/authStore";

export function ProfilePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { profile, isLoading, error, saveProfile } = useProfile();
  const [isPqrDialogOpen, setIsPqrDialogOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isSecurityRoute = location.pathname.endsWith("/security");

  const form = useForm<ProfileFormValues, undefined, ProfileSubmissionValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      language_pref: "en",
    },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone ?? "",
        language_pref: profile.language_pref,
      });
    }
  }, [form, profile]);

  async function onSubmit(values: ProfileSubmissionValues) {
    setMessage(null);
    setSubmitError(null);

    try {
      await saveProfile(values);
      setMessage(t("profile.saveSuccess"));
    } catch {
      setSubmitError(t("profile.saveError"));
    }
  }

  const pqrDefaultValues = {
    email: user?.email ?? "",
    name: [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim(),
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="font-display text-5xl text-ink">{t("profile.title")}</h1>
            <p className="mt-3 max-w-2xl text-lg leading-8 text-ink/65">
              {t("profile.subtitle")}
            </p>
          </div>
          <div className="flex gap-2 rounded-full border border-ink/10 bg-white/60 p-1">
            <NavLink
              to="/account/profile"
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition ${
                  isActive ? "bg-ink text-canvas" : "text-ink/60 hover:bg-ink/5 hover:text-ink"
                }`
              }
            >
              {t("profile.sections.profile")}
            </NavLink>
            <NavLink
              to="/account/billing"
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition ${
                  isActive ? "bg-ink text-canvas" : "text-ink/60 hover:bg-ink/5 hover:text-ink"
                }`
              }
            >
              {t("profile.sections.billing")}
            </NavLink>
            <NavLink
              to="/account/security"
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition ${
                  isActive ? "bg-ink text-canvas" : "text-ink/60 hover:bg-ink/5 hover:text-ink"
                }`
              }
            >
              {t("profile.sections.security")}
            </NavLink>
            <NavLink
              to="/account/requests"
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition ${
                  isActive ? "bg-ink text-canvas" : "text-ink/60 hover:bg-ink/5 hover:text-ink"
                }`
              }
            >
              {t("profile.sections.requests")}
            </NavLink>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <Panel className="space-y-5">
            {isSecurityRoute ? (
              <>
                <div>
                  <h2 className="font-display text-3xl text-ink">{t("profile.securityTitle")}</h2>
                  <p className="mt-2 text-base leading-7 text-ink/65">
                    {t("profile.securitySubtitle")}
                  </p>
                </div>
                <PasswordChangeForm />
                <AccountDeletionForm />
              </>
            ) : (
              <>
                <div>
                  <h2 className="font-display text-3xl text-ink">{t("profile.sections.profile")}</h2>
                  <p className="mt-2 text-base leading-7 text-ink/65">
                    {t("profile.languageHelp")}
                  </p>
                </div>
                <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="profile-first-name">{t("auth.fields.firstName")}</Label>
                      <Input id="profile-first-name" {...form.register("first_name")} />
                      {form.formState.errors.first_name ? (
                        <FormMessage>
                          {t(form.formState.errors.first_name.message ?? "auth.errors.generic")}
                        </FormMessage>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="profile-last-name">{t("auth.fields.lastName")}</Label>
                      <Input id="profile-last-name" {...form.register("last_name")} />
                      {form.formState.errors.last_name ? (
                        <FormMessage>
                          {t(form.formState.errors.last_name.message ?? "auth.errors.generic")}
                        </FormMessage>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="profile-phone">{t("auth.fields.phone")}</Label>
                    <Input id="profile-phone" {...form.register("phone")} />
                    {form.formState.errors.phone ? (
                      <FormMessage>
                        {t(form.formState.errors.phone.message ?? "auth.errors.generic")}
                      </FormMessage>
                    ) : null}
                  </div>

                  <div>
                    <Label htmlFor="profile-language">{t("common.language")}</Label>
                    <select
                      id="profile-language"
                      className="w-full rounded-3xl border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
                      {...form.register("language_pref")}
                    >
                      <option value="en">{t("common.languageNames.en")}</option>
                      <option value="es">{t("common.languageNames.es")}</option>
                      <option value="pt">{t("common.languageNames.pt")}</option>
                    </select>
                  </div>

                  {message ? <FormMessage className="text-olive">{message}</FormMessage> : null}
                  {submitError ? <FormMessage>{submitError}</FormMessage> : null}
                  {error && !submitError ? <FormMessage>{error}</FormMessage> : null}

                  <Button type="submit" disabled={form.formState.isSubmitting || isLoading}>
                    {form.formState.isSubmitting ? t("common.saving") : t("common.save")}
                  </Button>
                </form>
              </>
            )}
          </Panel>

          <Panel className="space-y-4">
            <h2 className="font-display text-3xl text-ink">{t("shell.home.eyebrow")}</h2>
            <p className="text-base leading-7 text-ink/65">
              {isSecurityRoute ? t("profile.securitySubtitle") : t("profile.languageHelp")}
            </p>
            <div className="space-y-3 text-sm text-ink/65">
              <p>{t("shell.home.panels.sessionBody")}</p>
              <p>{t("shell.home.panels.profileBody")}</p>
              <p>{t("shell.home.panels.securityBody")}</p>
              <p>{t("profile.billingHint")}</p>
            </div>
            <div className="grid gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsPqrDialogOpen(true)}>
                <LifeBuoy className="size-4" />
                {t("pqr.actions.contactUs")}
              </Button>
              <Button asChild type="button" variant="ghost">
                <NavLink to="/account/requests">
                  <MessageSquareMore className="size-4" />
                  {t("pqr.actions.openHistory")}
                </NavLink>
              </Button>
            </div>
          </Panel>
        </div>
      </div>
      <PQRFormDialog
        defaultValues={pqrDefaultValues}
        isAuthenticated
        onOpenChange={setIsPqrDialogOpen}
        open={isPqrDialogOpen}
      />
    </>
  );
}
